import type { WashedLocale } from '@washed/i18n';

const LANGUAGE_COUNTRY_FALLBACK: Record<string, string> = {
  de: 'DE',
  en: 'US',
  es: 'ES',
  fr: 'FR',
  ja: 'JP',
  pt: 'PT',
  zh: 'CN',
};

export interface TaxCountryOption {
  readonly code: string;
  readonly name: string;
}

export function detectTaxCountry(locale: string | undefined): string {
  const source = locale ?? (typeof navigator === 'undefined' ? undefined : navigator.language);
  if (!source) return 'US';

  const parts = source.replace('_', '-').split('-');
  const language = (parts[0] ?? '').toLowerCase();
  const region = parts
    .slice(1)
    .find((part) => /^[A-Za-z]{2}$/u.test(part))
    ?.toUpperCase();

  return region ?? LANGUAGE_COUNTRY_FALLBACK[language] ?? 'US';
}

// Region codes Intl can name that are not ISO 3166-1 billing countries Stripe accepts:
// groupings (UN, EU, euro zone, outlying Oceania) and exceptionally reserved territory codes.
const NON_COUNTRY_REGIONS = new Set(['AC', 'CP', 'DG', 'EA', 'EU', 'EZ', 'IC', 'QO', 'TA', 'UN']);

/** The current code for [code], or undefined when the engine can't canonicalize it. */
function canonicalRegion(code: string): string | undefined {
  try {
    return Intl.getCanonicalLocales(`und-${code}`)[0]?.split('-')[1];
  } catch {
    return undefined;
  }
}

export function listTaxCountries(locale: WashedLocale): readonly TaxCountryOption[] {
  const display = new Intl.DisplayNames([locale === 'fr' ? 'fr-FR' : 'en-US'], {
    type: 'region',
  });
  const unknownRegion = display.of('ZZ');
  const countries: TaxCountryOption[] = [];
  const seen = new Set<string>();

  for (let first = 65; first <= 90; first += 1) {
    for (let second = 65; second <= 90; second += 1) {
      const code = `${String.fromCharCode(first)}${String.fromCharCode(second)}`;
      const name = display.of(code);
      if (name === undefined || name === code || name === unknownRegion) continue;
      // Retired codes (DY Dahomey, HV Upper Volta, ZR Zaire, FX…) still have names and would
      // list a country twice and send Stripe a retired code; keep only the current code.
      if (NON_COUNTRY_REGIONS.has(code) || canonicalRegion(code) !== code) continue;
      if (seen.has(name)) continue;
      seen.add(name);
      countries.push({ code, name });
    }
  }

  return countries.sort((a, b) => a.name.localeCompare(b.name, locale));
}

export function countryRequiresTaxPostalCode(countryCode: string): boolean {
  return countryCode.toUpperCase() === 'US';
}
